import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
// Removed: import { Check } from 'react-feather';

// --- Inline SVG Check Icon Component ---
const CheckIcon = ({ className }) => (
    <svg 
        xmlns="http://www.w3.org/2000/svg" 
        width="24" 
        height="24" 
        viewBox="0 0 24 24" 
        fill="none" 
        stroke="currentColor" 
        strokeWidth="3" 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        className={className}
    >
        <polyline points="20 6 9 17 4 12"></polyline>
    </svg>
);


// --- Advanced AnimateOnScroll Component (No Changes) ---
const AnimateOnScroll = ({ children, direction = 'up', delay = 0, animationType = 'slide' }) => {
    const [isVisible, setIsVisible] = useState(false);
    const ref = useRef(null);

    const directionClasses = {
        up: 'translate-y-20', down: '-translate-y-20',
        left: '-translate-x-20', right: 'translate-x-20',
    };

    let animationInitialClasses = '';
    let animationFinalClasses = '';
    let useDirection = true;

    switch (animationType) {
        case 'slide-rotate':
            animationInitialClasses = direction === 'left' ? 'rotate-[3deg]' : 'rotate-[-3deg]';
            animationFinalClasses = 'rotate-0';
            break;
        case 'zoom':
            animationInitialClasses = 'scale-95';
            animationFinalClasses = 'scale-100';
            useDirection = false;
            break;
        default:
            break;
    }

    const initialClasses = `opacity-0 ${useDirection ? directionClasses[direction] : ''} ${animationInitialClasses}`;
    const finalClasses = `opacity-100 translate-y-0 translate-x-0 ${animationFinalClasses}`;

    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => {
            setIsVisible(entry.isIntersecting);
        }, { threshold: 0.1 });

        const currentRef = ref.current;
        if (currentRef) observer.observe(currentRef);

        return () => {
            if (currentRef) observer.unobserve(currentRef);
        };
    }, []);

    return (
        <div 
            ref={ref}
            className={`transition-all duration-700 ease-out ${isVisible ? finalClasses : initialClasses}`}
            style={{ transitionDelay: `${delay}ms` }}
        >
            {children}
        </div>
    );
};


const Pricing = () => {
    // --- Tailwind Color Mapping for Red/Black/White Theme ---
    const RED_ACCENT = "text-red-700";
    const DARK_TEXT = "text-gray-900";
    const LIGHT_BG = "bg-gray-50";

    return (
        // Changed background color and removed original style attribute
        <section id="services" className={`py-24 ${LIGHT_BG}`}>
            <div className="container mx-auto px-6 max-w-7xl">
                
                <AnimateOnScroll direction="up">
                    <div className="text-center mb-16">
                        <h2 className={`text-5xl md:text-6xl headline-font font-extrabold ${RED_ACCENT}`}>
                            Our Full Suite of Interior & Architectural Services
                        </h2>
                        <p className="text-xl mt-4 max-w-3xl mx-auto text-gray-700">
                            We offer specialized solutions in Madipakkam, Chennai, tailored to every project scale and complexity from full residential remodels to expert commercial space planning.
                        </p>
                    </div>
                </AnimateOnScroll>

                {/* --- Pricing Cards Grid (3 Cards for the 3 main business areas) --- */}
                <div className="grid lg:grid-cols-3 gap-8">
                    
                    {/* --- Card 1: Residential & Custom Projects (Core Offering) --- */}
                    <AnimateOnScroll animationType="zoom" delay={100}>
                        <div className={`pricing-card h-full p-8 text-center flex flex-col bg-white rounded-xl shadow-lg transition-all duration-500 hover:shadow-2xl hover:shadow-red-500/40 hover:-translate-y-2 cursor-pointer border-t-4 border-red-600`}>
                            <h3 className={`text-3xl headline-font font-bold ${RED_ACCENT}`}>
                                Residential & Custom Projects
                            </h3>
                            <p className="text-gray-500 mt-2 text-lg">Creating Functional, Personal Homes</p>
                            
                            <div className={`text-3xl font-bold my-6 ${DARK_TEXT}`}>
                                Ask for Personalized Quote
                            </div>
                            
                            <ul className="space-y-4 text-left mb-8 flex-grow text-gray-700">
                                <li className="flex items-start">
                                    <CheckIcon className={`w-5 h-5 mr-3 mt-1 flex-shrink-0 ${RED_ACCENT}`} /> 
                                    Full Turnkey Solutions: Flat & Residential Interiors (Complete end to end design and execution).
                                </li>
                                <li className="flex items-start">
                                    <CheckIcon className={`w-5 h-5 mr-3 mt-1 flex-shrink-0 ${RED_ACCENT}`} /> 
                                    Specialized Home Design: Apartment, Villa, Home Office, and Study Room Interior Design.
                                </li>
                                <li className="flex items-start">
                                    <CheckIcon className={`w-5 h-5 mr-3 mt-1 flex-shrink-0 ${RED_ACCENT}`} /> 
                                    Functional Decor: Modular Kitchens, Bathroom, Bedroom, and Puja Room Interior Design.
                                </li>
                                <li className="flex items-start">
                                    <CheckIcon className={`w-5 h-5 mr-3 mt-1 flex-shrink-0 ${RED_ACCENT}`} /> 
                                    Bespoke Furnishings: Bed Design & Creation, Custom Multiwood Design Cutting Services.
                                </li>
                            </ul>
                            
                            <Link to="/contact" state={{ serviceType: 'Residential Design' }} 
                                className={`w-full mt-auto px-6 py-3 text-lg font-semibold text-white bg-red-600 rounded-full shadow-lg transition-all duration-300 transform hover:scale-[1.03] hover:bg-red-700 hover:shadow-red-600/60`}>
                                Enquire Now
                            </Link>
                        </div>
                    </AnimateOnScroll>
                    
                    {/* --- Card 2: Commercial & Specialized Services (Most Popular) --- */}
                    <AnimateOnScroll animationType="zoom" delay={200}>
                        <div className="pricing-card h-full p-8 text-center flex flex-col relative bg-white rounded-xl shadow-2xl transform lg:scale-105 transition-all duration-500 hover:shadow-2xl hover:shadow-red-500/70 hover:scale-[1.08] cursor-pointer border-2 border-red-600">
                            
                            <div className="absolute top-0 -translate-y-2/3 left-1/2 -translate-x-1/2 bg-red-600 text-white px-4 py-2 text-sm font-bold uppercase tracking-wider rounded-full shadow-md">Most Popular</div>
                            
                            <h3 className={`text-3xl headline-font font-bold ${RED_ACCENT}`}>
                                Commercial & Space Planning
                            </h3>
                            <p className="text-gray-500 mt-2 text-lg">Maximizing Productivity & Brand Identity</p>
                            
                            <div className={`text-3xl font-bold my-6 ${DARK_TEXT}`}>
                                Ask for Personalized Quote
                            </div>
                            
                            <ul className="space-y-4 text-left mb-8 flex-grow text-gray-700">
                                <li className="flex items-start">
                                    <CheckIcon className={`w-5 h-5 mr-3 mt-1 flex-shrink-0 ${RED_ACCENT}`} /> 
                                    Corporate Space Planning: Optimizing layouts for offices, enterprises, and communication centers.
                                </li>
                                <li className="flex items-start">
                                    <CheckIcon className={`w-5 h-5 mr-3 mt-1 flex-shrink-0 ${RED_ACCENT}`} /> 
                                    Commercial Interiors: Design for offices, showrooms, clubhouses, and hospitality venues.
                                </li>
                                <li className="flex items-start">
                                    <CheckIcon className={`w-5 h-5 mr-3 mt-1 flex-shrink-0 ${RED_ACCENT}`} /> 
                                    Advanced Architecture: Business Interior Architecture and Steel Connection Design.
                                </li>
                                <li className="flex items-start">
                                    <CheckIcon className={`w-5 h-5 mr-3 mt-1 flex-shrink-0 ${RED_ACCENT}`} /> 
                                    Sustainable Design: Consultations for Eco Friendly Interior Designer solutions.
                                </li>
                            </ul>
                            
                            <Link to="/contact" state={{ serviceType: 'Commercial Design' }} 
                                className={`w-full mt-auto px-6 py-3 text-lg font-semibold text-white bg-red-600 rounded-full shadow-lg transition-all duration-300 transform hover:scale-[1.03] hover:bg-red-700 hover:shadow-red-600/60`}>
                                Get a Quote
                            </Link>
                        </div>
                    </AnimateOnScroll>
                    
                    {/* --- Card 3: Premium Detailing & Finishes --- */}
                    <AnimateOnScroll animationType="zoom" delay={300}>
                        <div className={`pricing-card h-full p-8 text-center flex flex-col bg-white rounded-xl shadow-lg transition-all duration-500 hover:shadow-2xl hover:shadow-red-500/40 hover:-translate-y-2 cursor-pointer border-t-4 border-red-600`}>
                            <h3 className={`text-3xl headline-font font-bold ${RED_ACCENT}`}>
                                Specialized Painting & Finishes
                            </h3>
                            <p className="text-gray-500 mt-2 text-lg">Meticulous Coatings & Artistic Touches</p>
                            
                            <div className={`text-3xl font-bold my-6 ${DARK_TEXT}`}>
                                Ask for Personalized Quote
                            </div>
                            
                            <ul className="space-y-4 text-left mb-8 flex-grow text-gray-700">
                                <li className="flex items-start">
                                    <CheckIcon className={`w-5 h-5 mr-3 mt-1 flex-shrink-0 ${RED_ACCENT}`} /> 
                                    Premium Painting: Interior, Exterior, Wood, Metal, and specialized surface painting (Chimney, Equipment).
                                </li>
                                <li className="flex items-start">
                                    <CheckIcon className={`w-5 h-5 mr-3 mt-1 flex-shrink-0 ${RED_ACCENT}`} /> 
                                    Ceiling & Structure: False Ceiling Installation, PVC Interior Services, and Ceiling Design.
                                </li>
                                <li className="flex items-start">
                                    <CheckIcon className={`w-5 h-5 mr-3 mt-1 flex-shrink-0 ${RED_ACCENT}`} /> 
                                    Artistic Finishes: Kerala Mural Hand Painting, Acrylic Art, and Custom Souvenir Design.
                                </li>
                                <li className="flex items-start">
                                    <CheckIcon className={`w-5 h-5 mr-3 mt-1 flex-shrink-0 ${RED_ACCENT}`} /> 
                                    Restoration: Statue Painting Services and Heritage Surface contracting.
                                </li>
                            </ul>
                            
                            <Link to="/contact" state={{ serviceType: 'Specialized Finishes' }} 
                                className={`w-full mt-auto px-6 py-3 text-lg font-semibold text-white bg-red-600 rounded-full shadow-lg transition-all duration-300 transform hover:scale-[1.03] hover:bg-red-700 hover:shadow-red-600/60`}>
                                Enquire Now
                            </Link>
                        </div>
                    </AnimateOnScroll>
                    
                </div>
            </div>
        </section>
    );
};

export default Pricing;
